import {Mental} from "../Mental";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {MentalHelper} from "../utils/MentalHelper";
import {TweenLite} from "gsap";
import {IMentalGameData, IMentalInitData} from "../server/MentalData";
import {ReelStopPresentation} from "../flowStatePresentations/ReelStopPresentation";
import {MentalGameMode} from "../server/MentalGameMode";
import {MentalReel} from "./MentalReel";
import {MentalNormalSymbol} from "./symbols/MentalNormalSymbol";
import {FeaturePresentationHelper} from "../utils/FeaturePresentationHelper";
import {MentalSymbolStates} from "./MentalSymbol";
import {WinRatioType} from "../../framework/winPresentation/IWinRatios";

export class ScatterAnticipationView extends PIXI.Container {

    private _standardFsAnticipationTop: TimelineSprite;
    private _standardFsAnticipationBottom: TimelineSprite;
    private _coolFsAnticipation: TimelineSprite;
    private _superFsAnticipation: TimelineSprite;
    private _s2passByReel1: TimelineSprite;
    private _s2passByReel3: TimelineSprite;
    private _isEligiblePassBy: boolean = false;

    constructor() {
        super();
        this.initAnimation();
    }

    private static createJpegSeq(name: string, animPos: number[], scaleY: number = 1): TimelineSprite {
        const jpegSeq: TimelineSprite = MentalHelper.createSprite(name);
        jpegSeq.anchor.set(0.5);
        jpegSeq.blendMode = PIXI.BLEND_MODES.ADD;
        jpegSeq.scale.y = scaleY;
        jpegSeq.position.set(animPos[0], animPos[1]);
        jpegSeq.hide();

        return jpegSeq;
    }

    private static createPngSeq(name: string): TimelineSprite {
        const pngSeq: TimelineSprite = MentalHelper.createSprite(name);
        pngSeq.anchor.set(0.5);
        pngSeq.position.set(650, 390);
        pngSeq.hide();

        return pngSeq;
    }

    private static createPassBySeq(animPos: number[]): TimelineSprite {
        const pngSeq: TimelineSprite = MentalHelper.createSprite("passBy");
        pngSeq.anchor.set(0.5, 0.6);
        pngSeq.position.set(animPos[0], animPos[1]);
        pngSeq.hide();
        return pngSeq;
    }

    private static unDimSymbols(): void {
        for (let reelId: number = 0; reelId < 5; reelId++) {
            const currReel: MentalReel = Mental.reelArea.getReel(reelId),
                allVisibleSyms: MentalNormalSymbol[] = (currReel.getAllVisibleStopSymbolsWithEnhancerSymbols()) as MentalNormalSymbol[],
                allSymsWithAllSplits: MentalNormalSymbol[] = FeaturePresentationHelper.getAllSymbolOnReelWithSplits(allVisibleSyms);

            allSymsWithAllSplits.forEach((currSym: MentalNormalSymbol) => {
                if (currSym && currSym.symbolName !== "S") {
                    currSym.currentTextureState.tint = 0xffffff;
                }
            });
        }
    }

    public checkForNearWin(): boolean {
        const gameData: IMentalGameData = Mental.model.gameData;

        let isFromFeatureBuy: boolean = gameData.wasFeatureBuy,
            flag: boolean = false;

        if (gameData.nearWinReels[4] && !isFromFeatureBuy && !(ReelStopPresentation.isQuickStop)) {
            flag = true;
        }

        return flag;
    }

    public playScatterAnticipation(): void {
        const s2Count: number = this.getS2Count();
        if (s2Count === 1) {
            this.playAnticipationAnim([this._coolFsAnticipation]);
            Mental.background.playWinFlickerAnim(WinRatioType.MEDIUM, -1);
        } else if (s2Count === 2) {
            this.playAnticipationAnim([this._superFsAnticipation]);
            Mental.background.playWinFlickerAnim(WinRatioType.LARGE, -1);
        } else {
            this.playAnticipationAnim([this._standardFsAnticipationTop, this._standardFsAnticipationBottom]);
        }
    }

    public stopScatterAnticipation(): void {
        TweenLite.to([this._standardFsAnticipationTop, this._standardFsAnticipationBottom, this._coolFsAnticipation, this._superFsAnticipation], {
            alpha: 0, duration: 0.1,
            onComplete: () => {
                this._standardFsAnticipationTop.stopLoop();
                this._standardFsAnticipationTop.hide();
                this._standardFsAnticipationBottom.stopLoop();
                this._standardFsAnticipationBottom.hide();

                this._coolFsAnticipation.stopLoop();
                this._coolFsAnticipation.hide();

                this._superFsAnticipation.stopLoop();
                this._superFsAnticipation.hide();
                Mental.background.stopWinFlicker();
                TweenLite.to({}, {
                    delay: 0.001,
                    onComplete: () => {
                        ScatterAnticipationView.unDimSymbols();
                    }
                })
            }
        });
    }

    public playPassByAnim(reelId: number): void {
        const gameData: IMentalGameData | IMentalInitData = Mental.model.data;

        if (this._isEligiblePassBy && !(ReelStopPresentation.isQuickStop)) {
            if (reelId === 1 && !(this._s2passByReel1.visible) && gameData.scatter2Position[reelId] === -1) {
                this._s2passByReel1.getAnimationAutoShowHide(true, true);
            }

            if (reelId === 3 && !(this._s2passByReel3.visible) && gameData.scatter2Position[reelId] === -1) {
                this._s2passByReel3.getAnimationAutoShowHide(true, true);
            }
        }
    }

    public onChangeMode(mode: string) {
        switch (mode) {
            case MentalGameMode.STANDARD_FREESPIN:
            case MentalGameMode.COOL_FREESPIN:
                this._isEligiblePassBy = true;
                break;
            default:
                this._isEligiblePassBy = false;
                break;
        }
    }

    public getS2Count(): number {
        const s2Positions: number[] = Mental.model.gameData.scatter2Position;

        return s2Positions.filter(function (x) {
            return x !== -1;
        }).length;
    }

    public dimSplitSyms(currSymbol: MentalNormalSymbol, currState: MentalSymbolStates): void {
        const splitSymbols: MentalNormalSymbol[] = currSymbol._splitSymbols;

        splitSymbols.forEach((splSym: MentalNormalSymbol) => {
            if (!(splSym.symbolName === "S" || splSym.symbolName === "S2")) {
                splSym.setState(currState);
            }
        });
    }

    private playAnticipationAnim(animType: TimelineSprite[]): void {
        for (let i: number = 0; i < animType.length; i++) {
            animType[i].alpha = 0;
            animType[i].show();
            animType[i].playLoop();
        }
        this.dimSymbols();

        TweenLite.to(animType, {
            alpha: 1,
            duration: 0.1
        });
    }

    private initAnimation(): void {
        this._standardFsAnticipationTop = ScatterAnticipationView.createJpegSeq("s1anticipation", [650, 250]);
        this._standardFsAnticipationBottom = ScatterAnticipationView.createJpegSeq("s1anticipation", [650, 530], -1);

        this._coolFsAnticipation = ScatterAnticipationView.createPngSeq("coolFsAnticipation");
        this._superFsAnticipation = ScatterAnticipationView.createPngSeq("superFsAnticipation");

        this._s2passByReel1 = ScatterAnticipationView.createPassBySeq([215, 420]);
        this._s2passByReel3 = ScatterAnticipationView.createPassBySeq([503, 420]);

        this.addChild(this._standardFsAnticipationTop, this._standardFsAnticipationBottom, this._coolFsAnticipation, this._superFsAnticipation, this._s2passByReel1, this._s2passByReel3);
    }

    private dimSymbols(): void {
        for (let reelId: number = 0; reelId < 5; reelId++) {
            const currReel: MentalReel = Mental.reelArea.getReel(reelId),
                allVisibleSyms: MentalNormalSymbol[] = (currReel.getAllVisibleStopSymbolsWithEnhancerSymbols()) as MentalNormalSymbol[],
                allSymsWithAllSplits: MentalNormalSymbol[] = FeaturePresentationHelper.getAllSymbolOnReelWithSplits(allVisibleSyms),
                s2Count: number = this.getS2Count();

            allSymsWithAllSplits.forEach((currSym: MentalNormalSymbol) => {
                if (!(currSym.symbolName === "S" || currSym.symbolName === "S2")) {
                    if (s2Count === 1) {
                        currSym.setState(MentalSymbolStates.COOL_FS_ANTICIPATION);
                    } else if (s2Count === 2) {
                        currSym.setState(MentalSymbolStates.SUPER_FS_ANTICIPATION);
                    } else {
                        currSym.setState(MentalSymbolStates.STANDARD_FS_ANTICIPATION);
                    }
                }
            });
        }
    }
}